import axios from "axios";
import {
  Box3,
  Color,
  DirectionalLight,
  Matrix4,
  OrthographicCamera,
  Quaternion,
  Raycaster,
  Scene,
  Vector3,
  WebGLRenderer,
} from "three";
import { Common } from "../../common/src";
import { RtkFixData } from "../../common/src/modules";
import { CameraManager } from "./camera/CameraManager";
import { ControlManager } from "./camera/controls/ControlManager";
import { IQuick3DCamera } from "./camera/quick3d-camera-interface";
import {
  IOrthographicFitParameters,
  Quick3DOrthographicCamera,
} from "./camera/quick3d-orthographic-camera";
import {
  CanvasPolygonSelection,
  ISelectionData,
} from "./Canvas/polygon-selection";
import {
  DEFAULT_POINT_SIZE,
  ITextScale,
  SCENE_TYPE,
  setViewType,
} from "./config/ViewerConfig";
import {
  MeasurementCommandHistory,
  SetMeasurementPointsCommand,
} from "./measurement/command/measurementCommandHistory";
import { CoordTracker } from "./measurement/CoordTracker";
import { MeasurementManager } from "./measurement/MeasurementManager";
import { MeasurementSelector } from "./measurement/MeasurementSelector";
import {
  IEditMeasurementParams,
  IMeasurement,
  IMeasurementHandler,
  IMeasurementVisibleParams,
  MeasurementType,
  TCreateMeasurementParams,
  TMeasurementEditObserver,
} from "./measurement/types";
import { VolumeCalculationMeasurement } from "./measurement/VolumeCalculationMeasurement";
import { ObjectManager } from "./object/ObjectManager";
import { Photoshot } from "./object/Photoshot";
import { PhotoshotManager } from "./object/PhotoshotManager";
import { PreviewManager } from "./object/PreviewManager";
import { IPointPickerCallback, PointPicker } from "./picker/PointPicker";
import { IVolumeInfo } from "./pointcloud/HeightMapHandler";
import { IPointCloudLoadParams } from "./pointcloud/loader/interface";
import { PointCloudManager } from "./pointcloud/PointCloudManager";
import { IPointColorRangeInfo } from "./pointcloud/PointCloudTile";
import { AxisUI } from "./ui/AxisUI";
import { TControlMode, TProjectionMode } from "./utils/camera-utils";
import { InputHandler } from "./utils/input-handler/input-handler";
import * as Utils from "./utils/utils";

// import Stats from "stats.js"; // for debug purpose

export class Viewer {
  private _canvas: HTMLCanvasElement;

  private _sceneMap: Map<SCENE_TYPE, Scene>;
  private _renderer: WebGLRenderer;

  // camera
  private _cameraManager: CameraManager;
  private _worldSpace: Matrix4;
  private _worldSpaceInverse: Matrix4;
  private _worldQuaternion: Quaternion;
  // private _controller: OrbitControls;
  // private _rotateControl: GenericTrackballRotateControl;
  private _controlManager: ControlManager;
  private _currentSelection: CanvasPolygonSelection;
  private _poligonIndicesCount: number = 0;
  private _poligonMaxCount: number = 0;

  public set poligonIndicesCount(v: number) {
    this._poligonIndicesCount = v;
  }

  public get poligonIndicesCount() {
    return this._poligonIndicesCount;
  }

  public set poligonMaxCount(v: number) {
    this._poligonMaxCount = v;
  }

  public get poligonMaxCount() {
    return this._poligonMaxCount;
  }

  private _objectManager: ObjectManager;
  private _previewManager: PreviewManager;
  private _measurementManager: MeasurementManager;
  public photoshotManager: PhotoshotManager;
  public _pointCloudManager: PointCloudManager;
  private _pointPicker: PointPicker;
  private _inputHandler: InputHandler;
  // private _stats: Stats;
  // private _pointCloudSettings: PointCloudSettings;
  private _abortController: AbortController;

  private _axisUI: AxisUI;

  private _measurementCommandHistory: MeasurementCommandHistory;

  private _renderLoopCount: number = 2;
  public debouncedOnCameraChange: (target: number) => void;
  private _isInit: boolean;
  private _loadId: number = 0;
  private _currentType: Common.POINT_CLOUD_TYPE;

  public testUIVal: number[];

  private _pointSize: number;
  private _measurementSelector: MeasurementSelector;

  public whileOperation: boolean;

  public getPointSize() {
    return this._pointSize;
  }
  public viewType = null as string;

  // 点群のサイズを変更
  public changePointSize(size: number): void {
    this._pointSize = size;
    this._pointCloudManager.setPointSize(size);
    this.updateRenderer();
  }

  public get loadId(): number {
    return this._loadId;
  }

  public getCurrentType(): Common.POINT_CLOUD_TYPE {
    return this._currentType;
  }

  public setCurrentType(v: Common.POINT_CLOUD_TYPE) {
    this._currentType = v;
  }

  public getActiveSelectionInfo() {
    return this._pointCloudManager.activeSelectionInfo;
  }

  public get pointMaterial() {
    return this._pointCloudManager.pointMaterial;
  }

  public changeTestUIVal(index: number, val: number) {
    this.testUIVal[index] = val;
  }

  public getTestUIVal(index: number) {
    return this.testUIVal[index];
  }

  constructor() {
    this.testUIVal = [0.5, 1.3, 0.1, 10.0, 1.0];

    this._pointSize = DEFAULT_POINT_SIZE;

    // this._pointCloudSettings = settings;
    this._sceneMap = new Map();
    this._sceneMap.set(SCENE_TYPE.BACKGROUND, new Scene());
    this._sceneMap.set(SCENE_TYPE.STREAM_POINTCLOUD, new Scene());
    this._sceneMap.set(SCENE_TYPE.STATIC_POINTCLOUD, new Scene());
    this._sceneMap.set(SCENE_TYPE.MEASUREMENT, new Scene());
    this._sceneMap.set(SCENE_TYPE.POINTPICK, new Scene());
    this._sceneMap.set(SCENE_TYPE.FOREGROUND, new Scene());
    this._objectManager = new ObjectManager(
      this._sceneMap.get(SCENE_TYPE.BACKGROUND),
      this
    );

    this._abortController = new AbortController();
    this._measurementCommandHistory = new MeasurementCommandHistory();

    this.debouncedOnCameraChange = Utils.debounce(() => {
      // 回転終了
      this._cameraManager.rotateEnd();
      this._onCameraChange();
    }, 300);
    this.photoshotManager = new PhotoshotManager();

    (window as any)["viewer"] = this;
  }

  public getCanvas(): HTMLCanvasElement {
    return this._canvas;
  }

  public Resize(): void {
    if (!this._canvas) {
      return;
    }
    this._canvas.style.height = "100%";
    this._canvas.style.width = "100%";
    this._renderer.setPixelRatio(window.devicePixelRatio);
    this._renderer.setSize(this._canvas.clientWidth, this._canvas.clientHeight);

    this._cameraManager.setAspect(
      this._canvas.clientWidth / this._canvas.clientHeight
    );
    this._axisUI.resize();
    if (this._currentSelection) {
      this._currentSelection.copyMainCanvasSize(this._canvas);
    }
    this.updateRenderer(1);
  }

  private SetCamera(bbox: Box3): void {
    this._cameraManager.camera.updateProjectionMatrix();
  }

  private InitRenderer(canvas: HTMLCanvasElement): void {
    this._canvas = canvas;
    // keep buffer Data
    let contextAttributes = {
      alpha: true,
      depth: true,
      stencil: false,
      antialias: false,
      preserveDrawingBuffer: true,
      powerPreference: "high-performance",
    } as WebGLContextAttributes;
    const gl = this._canvas.getContext(
      "webgl",
      contextAttributes
    ) as WebGLRenderingContext;

    this._renderer = new WebGLRenderer({
      alpha: true,
      premultipliedAlpha: false,
      canvas: canvas,
      context: gl,
    });
    this._renderer.autoClear = false;
    this._renderer.setPixelRatio(window.devicePixelRatio);
    this._renderer.setSize(this._canvas.clientWidth, this._canvas.clientHeight);
  }

  private InitCamera() {
    // camera
    const worldX = new Vector3(1, 0, 0);
    const worldY = new Vector3(0, 0, 1);
    const worldZ = new Vector3(0, -1, 0);
    this._worldSpace = new Matrix4().makeBasis(worldX, worldY, worldZ);
    this._worldSpaceInverse = new Matrix4().copy(this._worldSpace).invert();
    this._worldQuaternion = new Quaternion().setFromRotationMatrix(
      this._worldSpace
    );
    this._cameraManager = new CameraManager(
      this._worldQuaternion,
      this._inputHandler,
      this
    );
    const dirLight = new DirectionalLight(0xffffff, 2);
    dirLight.position.set(0, 0, 2000);
    dirLight.lookAt(0, 0, -1);
    this._cameraManager.targetObject.add(dirLight);
  }

  public enableControl(controls: {
    pan?: boolean;
    rotate?: boolean;
    dolly?: boolean;
  }) {
    const { pan, rotate, dolly } = controls;
    this._cameraManager.enableControl({ pan, rotate, dolly });
  }

  private InitEventListners(): void {
    // bind
    this.Resize = this.Resize.bind(this);

    document.addEventListener("startController", () => {
      this.whileOperation = true;
      // console.log("startController");
    });

    document.addEventListener("endController", () => {
      this.whileOperation = false;
      // console.log("endController");
    });

    // this._controller.addEventListener("start", () => {
    // console.log("start");
    // this.togglePickPointGuide(false);
    // });
    // this._controller.addEventListener("change", () => {
    //   // console.log("change");

    //   // this._flgRender = true;
    //   this.updateRenderer(1);
    // });
    // this._controller.addEventListener("end", () => {
    //   // this._pointCloudManager.onCameraChange(
    //   //   this._cameraManager.camera,
    //   //   new AbortController()
    //   // );
    //   // console.log("end");
    //   // this._onCameraChange();
    //   this.debouncedOnCameraChange(1000);
    //   // this._flgRender = true;
    //   this.updateRenderer();
    // });
    const ua = navigator.userAgent;
    if (
      ua.indexOf("Windows") > 0 ||
      (ua.indexOf("Macintosh") && !("ontouchend" in document))
    ) {
      setTimeout(() => {
        window.addEventListener("resize", this.Resize);
        this.Resize();
      }, 1000);
    }
  }

  singleMeasurementRender(): void {
    this._objectManager.animateText(this._cameraManager.camera);

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.MEASUREMENT),
      this._cameraManager.camera
    );
  }

  singleRender(): void {
    this._objectManager.animateText(this._cameraManager.camera);

    this._renderer.clear();

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.BACKGROUND),
      this._cameraManager.camera
    );

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.STATIC_POINTCLOUD),
      this._cameraManager.camera
    );

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.STREAM_POINTCLOUD),
      this._cameraManager.camera
    );

    // this._renderer.render(
    //   this._sceneMap.get(SCENE_TYPE.MEASUREMENT),
    //   this._cameraManager.camera
    // );

    // this._renderer.render(
    //   this._sceneMap.get(SCENE_TYPE.POINTPICK),
    //   this._cameraManager.camera
    // );

    this._axisUI.render();
  }

  private MainRender(): void {
    requestAnimationFrame(this.MainRender);
    // this._stats.begin();
    if (this._renderLoopCount > 0) {
      this._objectManager.animateText(this._cameraManager.camera);

      this._renderer.clear();

      this._renderer.render(
        this._sceneMap.get(SCENE_TYPE.BACKGROUND),
        this._cameraManager.camera
      );

      this._renderer.render(
        this._sceneMap.get(SCENE_TYPE.STATIC_POINTCLOUD),
        this._cameraManager.camera
      );

      this._renderer.render(
        this._sceneMap.get(SCENE_TYPE.STREAM_POINTCLOUD),
        this._cameraManager.camera
      );

      this._renderer.render(
        this._sceneMap.get(SCENE_TYPE.MEASUREMENT),
        this._cameraManager.camera
      );

      this._renderer.render(
        this._sceneMap.get(SCENE_TYPE.POINTPICK),
        this._cameraManager.camera
      );

      this._renderer.render(
        this._sceneMap.get(SCENE_TYPE.FOREGROUND),
        this._cameraManager.camera
      );

      this._axisUI.render();

      // this._flgRender = false;
      this._renderer.render(
        this.photoshotManager.scene,
        this._cameraManager.camera
      );
      --this._renderLoopCount;
    }
    // this._stats.end();
  }

  public Init(canvas: HTMLCanvasElement): void {
    // canvas.parentElement.appendChild(this._stats.dom);
    // set renderer
    this.InitRenderer(canvas);
    this.InitCamera();
    // init event
    this.InitEventListners();

    this._axisUI = new AxisUI(this._cameraManager.camera, this._renderer);
    this._axisUI.flipXY(true);

    this._objectManager.setCanvas(this._canvas);
    this._objectManager.InitPickPointsGuide();

    this._inputHandler = new InputHandler(this, this._canvas.parentElement);
    this._pointCloudManager = new PointCloudManager(
      this,
      // this._pointCloudSettings,
      { threshold: 0.001 }
    );
    this._previewManager = new PreviewManager(
      this._sceneMap.get(SCENE_TYPE.MEASUREMENT),
      this
    );
    this._pointPicker = new PointPicker(this, this._inputHandler);
    this._measurementManager = new MeasurementManager(
      this,
      this._previewManager,
      this._inputHandler
    );
    this._measurementSelector = new MeasurementSelector(
      this,
      this._canvas,
      this._measurementManager
    );

    // controller
    this._controlManager = new ControlManager(
      this,
      this._cameraManager,
      this._inputHandler
    );
    this._inputHandler.setControlManager(this._controlManager);

    this._cameraManager.camera.position.set(-100, -100, 100);
    this._cameraManager.camera.up.set(0, 0, 1);
    this._cameraManager.camera.lookAt(0, 0, 0);
    this.MainRender = this.MainRender.bind(this);
    this.MainRender();
    this.photoshotManager.init(canvas, this);

    this.singleRender = this.singleRender.bind(this);
  }

  public handleUpdate() {
    this.updateRenderer(1);
  }

  public getRenderer(): WebGLRenderer {
    return this._renderer;
  }

  public getScene(sceneType: SCENE_TYPE = SCENE_TYPE.BACKGROUND): Scene {
    return this._sceneMap.get(sceneType);
  }

  public getCamera(): IQuick3DCamera {
    return this._cameraManager.camera;
  }

  private clearScene() {
    this._sceneMap.forEach((key, value) => {
      if (
        value === SCENE_TYPE.POINTPICK &&
        this._inputHandler.getInputHandler().has("pickingMode")
      ) {
        return;
      } else {
        key.clear();
      }
    });
  }

  public clear() {
    this.clearScene();
    this._pointCloudManager.clear();
    this._pointCloudManager.resetHighlightSelectedPointCloudSection();
    this.updateRenderer(1);
    // this._pointCloudManager.clearPointSize();
  }

  public abortLoadPointCloud() {
    this._abortController.abort();
    this._abortController = new AbortController();
  }

  public async loadPointCloud(
    type: string,
    projectId: string,
    timestamp: string,
    params: IPointCloudLoadParams
  ) {
    //clear the existing pointcloud
    this.viewType = type;
    this._loadId++;

    const load = this._loadId;
    this.clear();
    this._isInit = true;
    await this._pointCloudManager.loadPointCloud(
      type,
      projectId,
      timestamp,
      params,
      this._abortController,
      load
    );
    this.SetCamera(this._pointCloudManager.bvh.rootTile.boundingBox);
    this.fitAllCamera();
    this._onCameraChange();
    this._isInit = false;
    this.updateRenderer();
    const event = new CustomEvent("pointloaded", { detail: this._currentType });
    document.dispatchEvent(event);
  }

  private async _onCameraChange() {
    if (!this._isInit) {
      this.abortLoadPointCloud();
    }
    // this._abortController.abort()
    // this._abortController = new AbortController()
    await this._pointCloudManager?.onCameraChange(
      this._cameraManager.camera,
      this._abortController
    );
    // this._flgRender = true;
    this.updateRenderer();

    // if (tiles && tiles.length > 0) {
    // const measurements = this._measurementManager.getMeasurementByType(
    //   MeasurementType.VOLUME_CALCULATION
    // );
    // if (measurements.length > 0) {
    //   for (const measurement of measurements) {
    //     const m = measurement as IVolumeCalculationMeasurement;
    //     this._pointCloudManager.bvh.rootTile.applyHeightBasedColor(
    //       m.pointColorRangeInfo,
    //       m.polygonMesh,
    //       new Box2().setFromPoints(
    //         m.polygonMesh.vertices as unknown as Vector2[]
    //       ),
    //       this._pointCloudManager.offsetMatrix
    //     );
    //     // for (const tile of tiles) {

    //     //   tile.applyHeightBasedColor(
    //     //     m.pointColorRangeInfo,
    //     //     m.polygonMesh,
    //     //     new Box2().setFromPoints(
    //     //       m.polygonMesh.vertices as unknown as Vector2[]
    //     //     ),
    //     //     this._pointCloudManager.offsetMatrix,
    //     //     false
    //     //   );
    //     // }
    //   }
    // }
    // }
  }

  public loadTrajectory(
    url: string,
    abortController: AbortController
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      await this._objectManager
        .loadTrajectory(url, abortController)
        .then(() => resolve())
        .catch(() => reject());
      this.updateRenderer();
    });
  }

  public unloadPhotoshots(): void {
    this.photoshotManager.clear();
    this.updateRenderer(1);
  }
  public async loadPhotoshots(url: string) {
    const offset = this.getOffset();
    const res = await axios.get(url, { withCredentials: true });
    if (this.viewType != "simple") {
      return;
    }
    const data = res.data;

    data.forEach((item: RtkFixData, index: number) => {
      const shot = new Photoshot(index, item.imageName);
      const matrix = Utils.rtkFixMatrixToThree(item.transformMatrix, offset);
      shot.applyMatrix4(matrix);
      // @ts-ignore
      shot.accuracy = item.accuracy;

      this.photoshotManager.addPhotoshot(shot);
    });
    this.updateRenderer(1);
  }

  setPhotoshotVisible(visible: boolean) {
    this.photoshotManager.setVisible(visible);
    this.updateRenderer(1);
  }

  public addPickPoint(id: string, name: string, position: Vector3) {
    this._pointPicker.addPickPoint(id, name, position);
    // this._objectManager.addPickPoint(id, name, position);
    // this._objectManager.animateText(this._cameraManager.camera);
    // const sleep = (second: number) =>
    //   new Promise((resolve) => setTimeout(resolve, second * 1000));
    // await sleep(0.5);
    // this._flgRender = true;
    this.updateRenderer();
  }

  public updatePickPoint(id: string, name: string, position: Vector3): void {
    this._pointPicker.updatePickPoint(id, name, position);
    // this._objectManager.updatePickPoint(id, name, position);
    // this._flgRender = true;
    this.updateRenderer();
  }

  public deletePickPoint(id: string): void {
    this._pointPicker.deletePickPoint(id);
    // this._objectManager.deletePickPoint(id);
    // this._flgRender = true;
    this.updateRenderer();
  }

  public deleteAllPickPoint(): void {
    this._pointPicker.deleteAllPickPoint();
    // this._objectManager.deleteAllPickPoint();
    // this._flgRender = true;
    this.updateRenderer();
  }

  public startPicking(callbacks: IPointPickerCallback) {
    if (this._inputHandler.getInputHandler().has("pickingMode")) {
      return;
    }
    this._pointPicker?.startPicking(callbacks);
  }

  public endPicking() {
    this._pointPicker?.endPicking();
    this.updateRenderer();
  }

  public startCoordTracking(callback: (res: Vector3) => void) {
    if (this._inputHandler?.getInputHandler().has("coordTracking")) {
      return;
    }

    this._inputHandler?.registerHandler(
      `coordTracking`,
      new CoordTracker(this, this._inputHandler, callback)
    );
  }

  public endCoordTracking() {
    this._inputHandler?.unregisterHandler(`coordTracking`);
  }

  public saveController(): void {
    // this._controller.saveState();
  }

  public resetController(): void {
    // this._controller.reset();
  }

  public fit(): void {
    this._cameraManager.resetCurrentCameraTransforms();
    const bbSize = this._pointCloudManager.getSize() || new Vector3(1, 1, 1);
    let bbLength = bbSize.length();
    const center = this._pointCloudManager.getCenter() || new Vector3(0, 0, 0);
    if (!this._pointCloudManager.bvh.rootTile) return;
    if (this._cameraManager.camera instanceof Quick3DOrthographicCamera) {
      const fitOrthographicParameters = {
        bottomPadding: 0,
        leftPadding: 0,
        rightPadding: 0,
        topPadding: 0,
        distanceToPlaceCamera: bbLength,
      } as IOrthographicFitParameters;
      this._cameraManager.camera.fit(
        this._pointCloudManager.bvh.rootTile.boundingBox,
        fitOrthographicParameters
      );
    } else {
      this._cameraManager.camera.fit(
        this._pointCloudManager.bvh.rootTile.boundingBox
      );
    }
    this.updateRenderer(1);
  }

  private fitAllCamera() {
    this._cameraManager.resetCurrentCameraTransforms();
    const bbSize = this._pointCloudManager.getSize() || new Vector3(1, 1, 1);
    let bbLength = bbSize.length();
    if (!this._pointCloudManager.bvh.rootTile) return;
    const fitOrthographicParameters = {
      bottomPadding: 0,
      leftPadding: 0,
      rightPadding: 0,
      topPadding: 0,
      distanceToPlaceCamera: bbLength,
    } as IOrthographicFitParameters;
    this._cameraManager.orthographicCamera.fit(
      this._pointCloudManager.bvh.rootTile.boundingBox,
      fitOrthographicParameters
    );
    const helper = this._cameraManager.perspectiveCamera.fit(
      this._pointCloudManager.bvh.rootTile.boundingBox
    );

    // this.getScene(SCENE_TYPE.BACKGROUND).add(helper);
    this.updateRenderer(1);
  }

  // public setPickPointPosition(
  //   index: number,
  //   x: number,
  //   y: number,
  //   z: number
  // ): void {
  //   this._objectManager.setPickPoint(index, new Vector3(x, y, z));
  //   // this._flgRender = true;
  //   this.updateRenderer();
  // }

  public RemovePointCloud(): void {
    this._objectManager.removePointCloud();
    // this._flgRender = true;
    this.updateRenderer();
  }

  // public TogglePointCloud(): void {
  //   this._objectManager.togglePointCloud();
  //   // this._flgRender = true;
  //   this.updateRenderer();
  // }

  public ToggleTrajectory(): void {
    this._objectManager.toggleTrajectory();
    // this._flgRender = true;
    this.updateRenderer();
  }

  public RemoveTrajectory(): void {
    this._objectManager.removeTrajectory();
    // this._flgRender = true;
    this.updateRenderer();
  }

  public intersectPointCloud(e: { clientX: number; clientY: number }): Vector3 {
    this.updateRenderer();
    const coords = Utils.CalculateMousePosition(e, this._canvas);
    const pickCoord = {
      x: ((coords.x + 1) * this._canvas.width) / 2,
      y: ((coords.y + 1) * this._canvas.height) / 2,
    };

    const gl: WebGLRenderingContext = this._canvas.getContext("webgl");

    return this._pointCloudManager.intersectPointCloud(gl, pickCoord);
  }

  public intersectPointCloudFromRay(e: Raycaster) {
    return this._pointCloudManager.intersectPointCloudFromRay(e);
  }

  public getThreshold() {
    const mode = Array.from(this._inputHandler.getInputHandler().keys())[0];
    if (mode !== "pickingMode") return 0.1;

    if (this._cameraManager.camera instanceof OrthographicCamera) {
      const scale =
        ((this._cameraManager.camera.right - this._cameraManager.camera.left) /
          this._cameraManager.camera.zoom) *
        0.002;
      return Math.min(scale, 0.05);
    }
    return 0.05;
  }

  public getPointCloudOffset() {
    return this._objectManager.getPointCloudOffset();
  }

  // public togglePickPointGuide(isShow: boolean): void {
  //   this._objectManager.togglePickPointGuide(isShow);
  //   // this._flgRender = true;
  //   this.updateRenderer();
  // }

  public updateRenderer(renderCount: number = 2): void {
    this._renderLoopCount = renderCount;
    // this._flgRender = true;
  }

  // public getPointSize(): number {
  //   return this._pointCloudManager.getPointSize();
  // }

  public deleteAllObject(): void {
    this._objectManager.deleteAllObject();
  }

  public updatePickPointGuide(position: Vector3) {
    this._pointPicker.updatePickPointGuide(position);
    this.updateRenderer();
  }

  public undoMeasurement() {
    const command = this._measurementCommandHistory.getUndoCommand();
    command && command.execute();
  }

  public redoMeasurement() {
    const command = this._measurementCommandHistory.getRedoCommand();
    command && command.execute();
  }

  public cancelEditingMeasurement() {
    this._measurementManager.cancelEditingMeasurement();
    // when the editing mode is active, undo all commands for turning back into original measurement
    while (true) {
      const command = this._measurementCommandHistory.getUndoCommand();
      if (!command) break;
      command.execute();
    }
    this._measurementCommandHistory.clear();
    this._measurementManager.changeAppearanceByState(
      this._measurementManager.activeMeasurement.id,
      "selected"
    );
  }

  public generateMeasurementId(): number {
    return this._measurementManager.generateMeasurementId();
  }

  public setMeasurementId(id: number) {
    this._measurementManager.setMeasurementId(id);
  }

  public initMeasurementMap() {
    this._measurementManager.initMeasurementMap();
  }

  public async createMeasurement(
    params: TCreateMeasurementParams,
    textScale: ITextScale
  ): Promise<IMeasurementHandler> {
    this.setPointsToMeasurement(undefined, []);
    this.changeAppearanceBySelected(null);
    const measurement = await this._measurementManager.createMeasurement(
      params,
      textScale
    );
    this._measurementCommandHistory.clear();
    return measurement;
  }

  public async createInitialMeasurement(
    params: TCreateMeasurementParams,
    data: IMeasurement,
    textScale: ITextScale
  ): Promise<IMeasurementHandler> {
    this.setPointsToMeasurement(undefined, []);
    const measurement = await this._measurementManager.createInitialMeasurement(
      params,
      data,
      textScale
    );
    this._measurementCommandHistory.clear();
    return measurement;
  }

  public editMeasurement(
    id: number,
    params: IEditMeasurementParams,
    observers: TMeasurementEditObserver[] = []
  ): void {
    const measurement = this._measurementManager.getMeasurementById(id);
    if (measurement) {
      this._measurementManager.editMeasurement(id, params, observers);
      this.setPointsToMeasurement(id, measurement._points);
    }
  }

  public setPointsToMeasurement(id: number | undefined, points: Vector3[]) {
    const setPointCommand = new SetMeasurementPointsCommand(
      this._measurementManager,
      {
        id,
        points: points.map((p) => p.clone()),
      }
    );
    setPointCommand.execute();
    this._measurementCommandHistory.add(setPointCommand);
    // this._measurementManager.setPoints(id, points);
  }

  public deleteMeasurement(ids?: number | number[]): number | number[] {
    return (
      this._measurementManager &&
      this._measurementManager.deleteMeasurement(ids)
    );
  }

  public changeAppearanceBySelected(id: number) {
    return (
      this._measurementManager &&
      this._measurementManager.changeAppearanceByState(id, "selected")
    );
  }

  public cancelMeasurement(): void {
    this._measurementManager?.cancelMeasurement();
  }

  public finishMeasurement() {
    this._measurementManager?.finishMeasurement();
    this._measurementCommandHistory.clear();
  }

  public resetMeasurement() {
    this._measurementManager?.resetMeasurement();
  }

  public unmark() {
    this._measurementManager?.unmark();
  }

  public async updateEditMeasurement(
    project_id: string,
    updateAt?: string,
    setUpdateAt?: (value: string) => void
  ) {
    await this._measurementManager.updateEditMeasurement(
      project_id,
      updateAt,
      setUpdateAt
    );
  }

  public finishMeasurementCreation() {
    this.finishMeasurement();
  }

  public setMeasurementVisibility(
    id: number,
    visibleParams: IMeasurementVisibleParams
  ) {
    this._measurementManager.setMeasurementVisibility(id, visibleParams);
  }

  public getMeasurementByType(type: MeasurementType): IMeasurement[] {
    return this._measurementManager.getMeasurementByType(type);
  }

  public calculateVolumeInfo(
    polygon: Vector3[],
    meshIndices: number[]
  ): IVolumeInfo {
    return this._pointCloudManager.calculateVolumeInfo(polygon, meshIndices);
  }

  public applyHeightBasedColor(
    polygon: { vertices: Vector3[]; indices: number[] },
    rangeInfo: IPointColorRangeInfo
  ) {
    this._pointCloudManager.applyHeightBasedColor(polygon, rangeInfo);
  }

  public setView(type: setViewType) {
    const center = this._pointCloudManager.getCenter().clone();
    const bbSize = this._pointCloudManager.getSize() || new Vector3(1, 1, 1);
    let bbLength = bbSize.length();
    if (type != setViewType.TOP_SAME_DIRECTION) {
      this.fit();
    }

    const camera = this._cameraManager.camera;

    switch (type) {
      case setViewType.FRONT:
        this._cameraManager.camera.position.set(
          center.x,
          center.y - bbLength * 2,
          center.z
        );
        this._cameraManager.camera.up.set(0, 0, 1);
        break;
      case setViewType.BACK:
        this._cameraManager.camera.position.set(
          center.x,
          center.y + bbLength,
          center.z
        );
        this._cameraManager.camera.up.set(0, 0, 1);
        break;
      case setViewType.RIGHT:
        this._cameraManager.camera.position.set(
          center.x + bbLength,
          center.y,
          center.z
        );
        this._cameraManager.camera.up.set(0, 0, 1);
        break;
      case setViewType.LEFT:
        this._cameraManager.camera.position.set(
          center.x - bbLength,
          center.y,
          center.z
        );
        this._cameraManager.camera.up.set(0, 0, 1);
        break;
      case setViewType.TOP:
        this._cameraManager.camera.position.set(
          center.x,
          center.y,
          center.z + bbLength
        );
        this._cameraManager.camera.up.set(0, 1, 0);
        break;
      case setViewType.BOTTOM:
        this._cameraManager.camera.position.set(
          center.x,
          center.y,
          center.z - bbLength
        );
        this._cameraManager.camera.up.set(0, -1, 0);

        break;

      case setViewType.TOP_SAME_DIRECTION: {
        camera.updateWorldMatrix(true, false);
        const forward = camera.getWorldDirection(new Vector3());

        const down = new Vector3(0, 0, -1);

        const right = down
          .clone()
          .cross(forward)
          .multiplyScalar(Math.sign(camera.up.z));

        const up = camera.up.clone();
        let newUp = up.clone();

        if (right.length()) {
          newUp = right.clone().cross(down).normalize();
        }
        this.fit();
        this._cameraManager.camera.position.set(
          center.x,
          center.y,
          center.z + bbLength
        );

        if (newUp.length() > 0) {
          camera.up.set(newUp.x, newUp.y, newUp.z);
        }
        break;
      }
      default:
        break;
    }

    this._cameraManager.camera.lookAt(center);
    this.updateRenderer();
  }

  public setHeightMapVisibility(visibility: boolean) {
    this._pointCloudManager.setHeightMapVisibility(visibility);
  }

  public setHeatMapVisibility(id: number, visibility: boolean) {
    const measurement = this._measurementManager.getMeasurementById(
      id
    ) as VolumeCalculationMeasurement;
    if (
      measurement &&
      measurement.type === MeasurementType.VOLUME_CALCULATION
    ) {
      measurement.heatMapVisibility = visibility;
      if (visibility) {
        this.applyHeightBasedColor(
          measurement.polygonMesh,
          measurement.pointColorRangeInfo
        );
      } else {
        this._pointCloudManager.setHeatMapVisibility(visibility);
        const measurements = this._measurementManager.getMeasurementByType(
          MeasurementType.VOLUME_CALCULATION
        );
        for (const m of measurements as VolumeCalculationMeasurement[]) {
          if (m.id !== id) {
            m.heatMapVisibility &&
              this.applyHeightBasedColor(m.polygonMesh, m.pointColorRangeInfo);
          }
        }
      }
    }

    this.updateRenderer(5);
  }

  public getOffset(): Vector3 {
    return this._pointCloudManager.bvh.offset;
  }

  public setPointCloudVisibility(visibility: boolean) {
    this._pointCloudManager.setPointCloudVisibility(visibility);
    this.updateRenderer(1);
  }

  public setProjectionMode(mode: TProjectionMode): void {
    this._cameraManager?.setProjectionMode(mode);
    this._axisUI?.setCamera(this._cameraManager.camera);

    this._onCameraChange();
  }

  public isCameraInit(): Boolean {
    return !!this._cameraManager && !!this._axisUI;
  }

  public getProjectionMode(): TProjectionMode {
    return this._cameraManager?.getProjectionMode();
  }

  public setCameraControl(mode: TControlMode): void {
    this._cameraManager.setControlMode(mode, this.getBoundingBox());
  }

  public setAxisVisiblity(visible: boolean) {
    this._axisUI.setVisibile(visible);
    this.updateRenderer(1);
  }

  public getBoundingRect(): DOMRect | ClientRect {
    return this._renderer.domElement.getBoundingClientRect();
  }

  public getCenter(): Vector3 {
    return this._pointCloudManager.bvh?.rootTile?.boundingBox.getCenter(
      new Vector3()
    );
  }

  public getBoundingBox(): Box3 {
    return this._pointCloudManager.bvh.rootTile.boundingBox;
  }

  public getRootLength(): number {
    return this._pointCloudManager?.bvh?.rootTile?.tileBBox
      .getSize(new Vector3())
      .length();
  }

  //canvas selection

  public startCanvasSelection(
    onSuccess: (data: ISelectionData) => void,
    onError: (message: string) => void,
    onPointClick?: (point: number[][], count: number, total?: number) => void
  ) {
    this._currentSelection = new CanvasPolygonSelection(this);
    // const onResolve = (params: ISelectionData) => {
    //   console.log(params);
    //   const planes = [];
    //   for(let i = 0; i< params.cartesianPoints.length; ++i){
    //     planes.push({normal: new Vector3().fromArray(params.normal[i]), position: new Vector3().fromArray(params.cartesianPoints[i])});
    //   }
    //   this.highlightSelectedPointCloudSection({planes}, new Color("red"))
    // };
    // const onReject = () => {};
    this._currentSelection.start(onSuccess, onError, onPointClick);
  }

  public finishCanvasSelection(): ISelectionData[] | undefined {
    if (this._currentSelection) {
      this._currentSelection.finish();
      const data = this._currentSelection.getTriangulatedSelectionData(this);
      if (data && data.length > 0) {
        return data;
      }
    }
  }

  public undoCanvasSelection() {
    if (this._currentSelection) {
      this._currentSelection.undoDrawing();
    }
  }

  public cancelCanvasSelection() {
    if (this._currentSelection) {
      this._currentSelection.resetDrawing();
    }
  }

  public exitCanvasSelection() {
    if (this._currentSelection) {
      // this._pointCloudManager.resetHighlightSelectedPointCloudSection();
      this._pointCloudManager.resetPointCloudColor();
      this._currentSelection?.dispose();
      this._currentSelection = undefined;
    }
    this.updateRenderer();
  }

  public resetHighlightSelectedPointCloudSection() {
    this._pointCloudManager.resetHighlightSelectedPointCloudSection();
  }

  // public getJSONOfCanvasSelection(): ISelectionData | undefined {
  //   if(this._currentSelection){
  //     const data = this._currentSelection.getSelectionData(this);
  //     return data.cartesianPoints.length > 0 ? data : undefined;
  //   }
  // }

  //points operation

  // public setColorToPointsUnderSelection(selectionPlanes:ISelectionData, color:string | number){
  //   const group = new Group();
  //   this.getScene(SCENE_TYPE.MEASUREMENT).add(group)
  //   // selectionPlanes.forEach(p=>{
  //     // const helper = new PlaneHelper(selectionPlanes[0], 10);
  //     // group.add(helper);
  //   // })

  //     const planes = [];
  //     for(let i = 0; i< selectionPlanes.cartesianPoints.length; ++i){
  //       planes.push({normal: new Vector3().fromArray(selectionPlanes.normal[i]), position: new Vector3().fromArray(selectionPlanes.cartesianPoints[i])});
  //     }
  //   this._pointCloudManager.highlightSelectedPointCloudSection({planes}, new Color(color));
  // }

  // public selectPointsInsideSelection(
  //   selectionData: ISelectionData[],
  //   color: string | number
  // ) {
  //   // const group = new Group();
  //   // this.getScene(SCENE_TYPE.MEASUREMENT).add(group);
  //   // selectionData.forEach(p=>{
  //   // const helper = new PlaneHelper(selectionData[0], 10);
  //   // group.add(helper);
  //   // })
  //   const convexShapes = [];
  //   for (const data of selectionData) {
  //     const planes: any[] = [];
  //     for (let i = 0; i < data.cartesianPoints.length; ++i) {
  //       planes.push({
  //         normal: new Vector3().fromArray(data.normal[i]),
  //         position: new Vector3().fromArray(data.cartesianPoints[i]),
  //       });
  //     }
  //     convexShapes.push({
  //       planes: planes,
  //       isSelectInside: true,
  //     });
  //   }
  //   // this._pointCloudManager.highlightSelectedPointCloudSection(
  //   //   { convexShapes },
  //   //   new Color(color),
  //   //   true
  //   // );
  //   this.updateRenderer(2);
  // }

  // public selectPointsOutsideSelection(
  //   selectionData: ISelectionData[],
  //   color: string | number
  // ) {
  //   const group = new Group();
  //   this.getScene(SCENE_TYPE.MEASUREMENT).add(group);
  //   // selectionData.forEach(p=>{
  //   // const helper = new PlaneHelper(selectionData[0], 10);
  //   // group.add(helper);
  //   // })
  //   const convexShapes = [];
  //   for (const data of selectionData) {
  //     const planes: any[] = [];
  //     for (let i = 0; i < data.cartesianPoints.length; ++i) {
  //       planes.push({
  //         normal: new Vector3().fromArray(data.normal[i]),
  //         position: new Vector3().fromArray(data.cartesianPoints[i]),
  //       });
  //     }
  //     convexShapes.push({
  //       planes: planes,
  //       isSelectInside: false,
  //     });
  //   }
  //   // this._pointCloudManager.highlightSelectedPointCloudSection(
  //   //   { convexShapes },
  //   //   new Color(color),
  //   //   false
  //   // );
  //   this.updateRenderer(2);
  // }

  public applySelectionsToPointCloud(
    selections: {
      triangles: ISelectionData[];
      isDeleteInside: boolean;
      isDeleted: boolean;
    }[],
    color: string | number
  ) {
    // selectionData.forEach(p=>{
    // const helper = new PlaneHelper(selectionData[0], 10);
    // group.add(helper);
    // })
    const convexShapes = [];
    const shapeCounts = [];
    for (const selection of selections) {
      for (const data of selection.triangles) {
        const planes: any[] = [];
        for (let i = 0; i < data.cartesianPoints.length; ++i) {
          planes.push({
            normal: new Vector3().fromArray(data.normal[i]),
            position: new Vector3().fromArray(data.cartesianPoints[i]),
          });
        }
        convexShapes.push({
          planes: planes,
          isSelectInside: selection.isDeleteInside,
          isDeleted: selection.isDeleted,
        });
      }
      shapeCounts.push(selection.triangles.length);
    }
    this._pointCloudManager.highlightSelectedPointCloudSection(
      { convexShapes, shapeCounts },
      new Color(color)
    );
    this.updateRenderer(2);
  }

  public removeSelectedPoints(selectionColor: number | string) {
    this._pointCloudManager.removeSelectedPoints(new Color(selectionColor));
    this.updateRenderer(2);
  }

  public resetPointsRemovalProcess() {
    //resets the points and canvas to select again
    //resetPointcloud
    //resetDrawing
  }

  public setMaxIterations(value: number) {
    this._pointCloudManager.pointMaterial.maxIterations = value;
  }

  public getMaxIterations() {
    return this._pointCloudManager.pointMaterial.maxIterations;
  }

  public flipXY(yUp: boolean) {
    this._axisUI.flipXY(yUp);
    this.updateRenderer(1);
  }

  public async setTileSplit(thresHold: number) {
    await this._pointCloudManager.setTileSplit(thresHold);
    this._onCameraChange();
  }

  public getTileSplit() {
    return this._pointCloudManager.getTileSplit();
  }

  public toggleIsDebug() {
    this._pointCloudManager.toggleIsDebug();
  }

  public toggleIsRotateDebug() {
    this._cameraManager.toggleIsRotateDebug();
  }

  public setBackgroundColor(color: number, alpha?: number) {
    if (this._renderer) {
      this._renderer.setClearColor(color, alpha);
      this.updateRenderer(1);
    }
  }
}
